/**
 * <EmployeePicker /> — typeahead selector for picking ONE employee.
 *
 * Drop in anywhere the system asks for an employee_id. Searches by
 * name (EN + AR) and employee_code. Shows photo + name + code +
 * status in the dropdown. Debounced. Keyboard navigable.
 *
 *   <EmployeePicker
 *     value={employeeId}
 *     onChange={(emp) => setEmployeeId(emp?.id ?? null)}
 *     placeholder="Search by name or code…"
 *   />
 *
 * The selected employee is fetched on mount when `value` is set,
 * so callers can pass a bare ID without needing the full DTO.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Search, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { api, type EmployeePickerOption } from "../../lib/api";
import { EmployeeAvatar } from "./EmployeeAvatar";

export interface EmployeePickerProps {
  value?: string | null;
  onChange: (emp: EmployeePickerOption | null) => void;
  legalEntityId?: string;
  includeTerminated?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Selected employee object — provide if you already have it,
   * otherwise the picker fetches it from `value`. */
  selected?: EmployeePickerOption | null;
}

export function EmployeePicker({
  value,
  onChange,
  legalEntityId,
  includeTerminated = false,
  placeholder,
  disabled,
  selected: selectedProp,
}: EmployeePickerProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const effectivePlaceholder =
    placeholder ?? t("app.epp_placeholder_default");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  // Fully controlled: `selected` is derived from `value`. No internal
  // state to drift out of sync on reorder / swap. The hydration query
  // loads the full employee list once (shared via react-query cache
  // across every picker instance on the page), and we look up `value`
  // in it. When the parent's `value` changes, the lookup re-runs and
  // the right employee shows up.
  const hydrationQ = useQuery({
    queryKey: ["employee-picker-hydrate", legalEntityId],
    enabled: !!value && !selectedProp && !!legalEntityId,
    staleTime: 60_000,
    queryFn: () =>
      api.listEmployeesForPicker({
        legalEntityId,
        includeTerminated: true,
        limit: 200,
      }),
  });
  const selected: EmployeePickerOption | null = (() => {
    if (selectedProp !== undefined && selectedProp !== null) return selectedProp;
    if (!value) return null;
    return hydrationQ.data?.find((e) => e.id === value) ?? null;
  })();

  // Search results — debounced query.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  const resultsQ = useQuery({
    queryKey: [
      "employee-picker-search",
      debouncedQ,
      legalEntityId,
      includeTerminated,
    ],
    enabled: open,
    queryFn: () =>
      api.listEmployeesForPicker({
        q: debouncedQ || undefined,
        legalEntityId,
        includeTerminated,
        limit: 20,
      }),
  });
  const results = resultsQ.data ?? [];

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const handlePick = useCallback(
    (emp: EmployeePickerOption) => {
      // Seed the hydration cache so the derive lookup finds the
      // employee on the very next render — without this the chip
      // would briefly fall back to the search input (since the
      // hydration query hadn't run yet for the new value).
      qc.setQueryData<EmployeePickerOption[] | undefined>(
        ["employee-picker-hydrate", legalEntityId],
        (prev) => {
          if (!prev) return [emp];
          if (prev.some((p) => p.id === emp.id)) return prev;
          return [...prev, emp];
        },
      );
      onChange(emp);
      setOpen(false);
      setQuery("");
    },
    [onChange, qc, legalEntityId],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery("");
  }, [onChange]);

  return (
    <div ref={wrapRef} className="relative w-full">
      {selected && !open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="w-full flex items-center gap-2 rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-start hover:border-brand-blue/50 disabled:opacity-50"
        >
          <EmployeeAvatar
            employeeId={selected.id}
            photoUrl={selected.photo_url}
            firstName={selected.first_name_en}
            lastName={selected.last_name_en}
            size={28}
          />
          <div className="flex-1 min-w-0">
            <div className="truncate text-sm font-medium text-text-primary">
              {selected.first_name_en} {selected.last_name_en}
            </div>
            <div className="truncate text-xs text-text-tertiary font-mono">
              {selected.employee_code}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="p-1 rounded hover:bg-neutral-100"
            aria-label={t("app.epp_clear_selection")}
          >
            <X size={14} className="text-text-tertiary" />
          </button>
          <ChevronDown size={14} className="text-text-tertiary" />
        </button>
      ) : (
        <div className="flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-default px-3 py-2 focus-within:border-brand-blue">
          <Search size={14} className="text-text-tertiary shrink-0" />
          <input
            id={inputId}
            type="text"
            autoFocus={open}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={effectivePlaceholder}
            disabled={disabled}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-tertiary"
          />
          {selected && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-neutral-100"
              aria-label={t("app.epp_clear")}
            >
              <X size={14} className="text-text-tertiary" />
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border-subtle bg-white shadow-xl max-h-64 overflow-y-auto">
          {resultsQ.isLoading && (
            <p className="px-3 py-2 text-xs text-text-tertiary">
              {t("app.gs_searching")}
            </p>
          )}
          {!resultsQ.isLoading && results.length === 0 && (
            <p className="px-3 py-2 text-xs text-text-tertiary">
              {debouncedQ ? t("app.epp_no_matches") : t("app.epp_start_typing")}
            </p>
          )}
          {results.map((emp) => (
            <button
              key={emp.id}
              type="button"
              onClick={() => handlePick(emp)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 text-start"
            >
              <EmployeeAvatar
                employeeId={emp.id}
                photoUrl={emp.photo_url}
                firstName={emp.first_name_en}
                lastName={emp.last_name_en}
                size={28}
              />
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium text-text-primary">
                  {emp.first_name_en} {emp.last_name_en}
                  {emp.first_name_ar && (
                    <span
                      className="ms-2 text-text-secondary font-normal"
                      dir="rtl"
                    >
                      ({emp.first_name_ar} {emp.last_name_ar})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-text-tertiary">
                  <span className="font-mono">{emp.employee_code}</span>
                  <span>·</span>
                  <span className="capitalize">{emp.employment_status}</span>
                  {emp.is_saudi_national && <span>🇸🇦</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
